import CresAxios from "../../../../util/CresAxios";
import EnterpriseComponent from "../../EnterpriseComponent";
import {MAX_COLUMNS} from "./reportResultColumns";

// The rows a report returned, as something PlatformTable can render.
//
// NO IDOC, like LookupEnterpriseComponent and LookupValueEnterpriseComponent before it: the service
// surface is plain JSON on t10/v1/reports, and a component that wraps an existing surface is the
// established arrangement in this app rather than a departure from it.
//
// COLUMNS ARE POSITIONS, NOT NAMES. A report's columns change with every run, and an
// EnterpriseComponent declares its attributes once at construction -- so the buffer carries c0..c11
// and the APPLET decides what each is called from the Columns the service returned beside the rows.
// The alternative is a component rebuilt per query, which means a grid remounted per query, which
// loses the reader's scroll position and column widths every time they change an aggregation.
//
// THE CAP LIVES IN A LEAF MODULE, not here. Declaring it here and letting the metadata import it
// closed a cycle -- metadata -> component -> EnterpriseComponent -> metadata factory -> metadata --
// which webpack resolved to a BLANK PAGE on every route. tsc and the production build were both
// clean; only loading the page found it. See reportResultColumns.
//
// POST TO READ, which looks wrong and is not. A specification is a structured document -- filters,
// values, nested column references -- and a GET would put all of it in a query string, where it is
// length-capped and logged by every hop in between. Filter values are tenant data.
const URL = "t10/v1/reports";

class ReportResultEnterpriseComponent extends EnterpriseComponent {

    // THE SPECIFICATION TO RUN, pushed on by the page before it queries. Without one there is
    // nothing to ask for -- and asking for "everything" is not the same question.
    setQuery = (query) => { this.query = query; this.storedResult = null; };

    // A RUN THAT ALREADY HAPPENED, rendered without asking the server to compute anything.
    //
    // This is the whole reason the rows were stored. Re-running a report to show what it said in
    // March would answer with today's data under March's heading, which is the specific lie this
    // phase exists to prevent. So a stored run is pushed onto the component and the next query
    // serves it from memory.
    setStoredResult = (run) => {
        this.storedResult = run;
        this.query = null;
    };

    // What the last run said its columns were, for the applet to label with.
    getResultColumns = () => this.resultColumns || [];

    // THE RECORD EACH ROW IS, or an empty list. Only a listing has them: the server sends a key
    // per row for that shape and for no other, so this is its answer rather than the page's guess.
    // A FALLBACK, not the source -- see the note in ReportResultTableApplet about why the page
    // passes these in.
    getRecordKeys = () => this.recordKeys || [];

    // Whether the last run hit the row cap.
    isTruncated = () => !!this.truncated;

    performQuery = (page, pageSize) => {
        page = page || 1;
        pageSize = pageSize || this.getDefaultCacheSize();

        this.notifyLoadStartListeners();

        if (this.storedResult) {
            const columns = (this.storedResult.Columns ?? []).slice(0, MAX_COLUMNS);
            const rows = this.storedResult.Rows ?? [];
            this.resultColumns = columns;
            this.truncated = !!this.storedResult.Truncated;

            const keys = this.storedResult.RecordKeys ?? [];
            this.recordKeys = keys;
            const results = rows.map((row, index) => {
                const record = {USID: `s${index}`, _record: keys[index] ?? ""};
                columns.forEach((_column, position) => {
                    const value = row[position];
                    record[`c${position}`] = value === null || value === undefined ? "" : String(value);
                });
                return record;
            });
            this.fetchEnterpriseComponentSuccess({
                Count: results.length,
                Results: results.slice((page - 1) * pageSize, page * pageSize),
            }, page, pageSize);
            return Promise.resolve();
        }

        if (!this.query) {
            // NOT AN ERROR. A grid mounted before anybody has built a report has nothing to show,
            // and an empty result is the honest rendering of that.
            this.resultColumns = [];
            this.fetchEnterpriseComponentSuccess({Count: 0, Results: []}, page, pageSize);
            return Promise.resolve();
        }

        return CresAxios({method: "POST", url: URL, json: true, data: {Query: this.query}})
            .then(response => {
                const columns = (response.data?.Columns ?? []).slice(0, MAX_COLUMNS);
                const rows = response.data?.Rows ?? [];
                this.resultColumns = columns;
                this.truncated = !!response.data?.Truncated;

                // POSITIONAL, and keyed by the SERVER's own column order. Reading the row by index
                // rather than by name is what lets one fixed set of attributes carry any report.
                // BESIDE THE ROWS, and absent on a summary -- where a row is a group and there
                // is no single record to open. An empty key is how a row says it cannot be opened,
                // which the grid reads rather than being told separately.
                const keys = response.data?.RecordKeys ?? [];
                this.recordKeys = keys;

                const results = rows.map((row, index) => {
                    const record = {USID: `r${index}`, _record: keys[index] ?? ""};
                    columns.forEach((_column, position) => {
                        const value = row[position];
                        record[`c${position}`] = value === null || value === undefined ? "" : String(value);
                    });
                    return record;
                });

                // PAGED HERE, because the service does not page -- the same trade, and the same
                // note, as LookupEnterpriseComponent and the hierarchies. A report is capped at ten
                // thousand rows, which is a size this can hold.
                this.fetchEnterpriseComponentSuccess({
                    Count: results.length,
                    Results: results.slice((page - 1) * pageSize, page * pageSize),
                }, page, pageSize);
            })
            .catch(error => this.fetchEnterpriseComponentFailed(error));
    };
}

export default ReportResultEnterpriseComponent;
